import { useEffect, useMemo, useState } from "react";
import { api, type Board, type Horizon, type Session, type Task } from "../../api.ts";
import { useTheme } from "../../glassTheme.tsx";
import { longDate } from "../../theme.ts";
import { starvingValues } from "../../starvingValues.ts";
import { Panel } from "./Panel.tsx";
import { TaskRow } from "./Row.tsx";
import { FlagCard } from "./FlagCard.tsx";
import { SessionWrite } from "./SessionWrite.tsx";
import { weeklyPrompt } from "./weeklyPrompt.ts";

const PREAMBLE =
  "This is my weekly reflection. Read what I say, log anything worth a note and " +
  "update any tasks it affects using the tools available — nothing that isn't " +
  "there already. This is not a productivity review: no score, no comparison to " +
  "last week. Reply in under four lines. What I'm thinking: ";

const effectiveDate = (t: Task) => (t.state === "waiting" && t.follow_up_on ? t.follow_up_on : t.due);

function groupByDay(tasks: Task[]): { date: string; tasks: Task[] }[] {
  const byDay = new Map<string, Task[]>();
  for (const t of tasks) {
    const d = effectiveDate(t);
    if (!d) continue;
    let list = byDay.get(d);
    if (!list) { list = []; byDay.set(d, list); }
    list.push(t);
  }
  return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, tasks]) => ({ date, tasks }));
}

export function Weekly({
  board, today, onOpen, doneToday, onWritten, onFlagsChanged,
}: {
  board: Board; today: string; onOpen: (id: string) => void;
  doneToday: Session | null; onWritten: () => void; onFlagsChanged: () => void;
}) {
  const { colors } = useTheme();
  const [horizon, setHorizon] = useState<Horizon | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.horizon(21).then((h) => live && setHorizon(h)).catch((e) => live && setError(e?.message ?? "could not load the horizon"));
    return () => { live = false; };
  }, []);

  const days = useMemo(() => (horizon ? groupByDay(horizon.dated) : []), [horizon]);
  const flag = board.flags.find((f) => f.state === "open") ?? null;
  const starving = useMemo(() => starvingValues(board), [board]);

  return (
    <div>
      <Panel title="Horizon">
        {error && <p style={{ margin: 0, fontSize: 13, color: "#9C4A3C" }}>{error}</p>}
        {!horizon && !error && <p style={{ margin: 0, fontSize: 13, color: colors.ink2 }}>Looking…</p>}
        {horizon && horizon.lead_time.length > 0 && (
          <>
            <div style={{ fontSize: 12.5, color: colors.ink2, marginBottom: 4 }}>Must start now</div>
            {horizon.lead_time.map((t) => (
              <TaskRow
                key={t.id} task={t} onOpen={() => onOpen(t.id)}
                meta={`starts now for ${t.starts_now_for.map((s) => s.title).join(", ")}`}
              />
            ))}
          </>
        )}
        {days.map(({ date, tasks }) => (
          <div key={date} style={{ marginTop: 10 }}>
            <div style={{
              fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600,
              color: colors.ink, padding: "8px 0 2px",
            }}>{longDate(date)}</div>
            {tasks.map((t) => (
              <TaskRow key={t.id} task={t} onOpen={() => onOpen(t.id)} right={t.follow_up_on === date ? "chase" : undefined} />
            ))}
          </div>
        ))}
        {horizon && days.length === 0 && horizon.lead_time.length === 0 && (
          <p style={{ margin: 0, fontSize: 13, color: colors.ink2 }}>Nothing dated in the next 21 days.</p>
        )}
      </Panel>

      <Panel title="A decision">
        {flag
          ? <FlagCard flag={flag} today={today} onResolved={onFlagsChanged} />
          : <p style={{ margin: 0, fontSize: 13, color: colors.ink2 }}>Nothing open.</p>}
      </Panel>

      <Panel title="Reflection">
        {starving.length > 0 && (
          <p style={{ margin: "0 0 10px", fontSize: 12.5, color: colors.ink2 }}>
            Nothing serving: {starving.map((v) => v.name).join(", ")}
          </p>
        )}
        {doneToday ? (
          <DoneSummary session={doneToday} />
        ) : (
          <SessionWrite
            kind="weekly" placeholder="What are you thinking" preamble={PREAMBLE}
            prompt={weeklyPrompt(today)} onWritten={onWritten}
          />
        )}
      </Panel>
    </div>
  );
}

function DoneSummary({ session }: { session: Session }) {
  const { colors } = useTheme();
  const wrote = session.wrote as { did?: string[] }[];
  const did = wrote.flatMap((w) => w.did ?? []);
  return (
    <p style={{ margin: 0, fontSize: 14, color: colors.ink2 }}>
      {did.length > 0 ? `Recorded this week: ${did.join(" · ")}` : "Recorded this week. Nothing was reported."}
    </p>
  );
}
